/**
 * AppFeedbackPage - Page component for displaying individual app feedback
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppData, useApiService } from '../../services/apiService';
import AppStatus from '../atoms/AppStatus';
import { Button } from '../atoms/Button';
import { Card } from '../atoms/Card';
import { ErrorMessage } from '../atoms/ErrorMessage';
import FeedbackTable from '../organisms/FeedbackTable';

interface AppFeedbackPageProps {
  className?: string;
}

const AppFeedbackPage: React.FC<AppFeedbackPageProps> = ({ className = '' }) => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const apiService = useApiService();
  
  // State management
  const [app, setApp] = useState<AppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  
  // Feedback state
  const [feedback, setFeedback] = useState<any[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch app data
  const fetchApp = async () => {
    if (!appId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getAppById(appId, true);
      setApp(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  // Fetch feedback data
  const fetchFeedback = async (page: number = 1) => {
    if (!appId) return;
    
    try {
      setFeedbackLoading(true);
      setFeedbackError(null);
      
      const response = await apiService.getFeedbackList({
        app_id: appId,
        page: page,
        per_page: 10,
        include_user: true,
        include_app: true,
        include_comments: false
      });
      
      setFeedback(response.data.items);
      setFeedbackTotal(response.data.total);
      setCurrentPage(response.data.page);
      setTotalPages(response.data.pages);
    } catch (err) {
      setFeedbackError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Sync app from Auth0
  const handleSyncApp = async () => {
    if (!app?.client_id) return;
    
    try {
      setSyncing(true);
      setSyncError(null);
      
      const response = await apiService.syncApps([app.client_id], true);
      
      if (response.data.synced_apps > 0) {
        // Refresh app data after sync
        await fetchApp();
        // Also refresh feedback in case app metadata was updated
        await fetchFeedback(currentPage);
      } else {
        setSyncError('Failed to sync application from Auth0');
      }
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to sync application');
    } finally {
      setSyncing(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchApp();
    fetchFeedback();
  }, [appId]);

  const getAppStatus = (app: AppData) => {
    if (!app.is_active) return 'inactive';
    if (!app.is_enabled) return 'error';
    return 'active';
  };

  const getAppTypeIcon = (appType?: string) => {
    switch (appType) {
      case 'spa':
        return '🌐';
      case 'native':
        return '📱';
      case 'regular_web':
        return '💻';
      case 'machine_to_machine':
        return '🤖';
      default:
        return '📱';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" role="status" aria-label="Loading"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading application...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <ErrorMessage title="Error" message={error} />
        <div className="mt-4 space-x-2">
          <Button onClick={() => fetchApp()}>
            Try Again
          </Button>
          <Button onClick={() => navigate('/apps')} variant="secondary">
            Back to Apps
          </Button>
        </div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <div className="text-4xl mb-2">📱</div>
        <div className="text-lg font-medium mb-1">Application not found</div>
        <div className="text-sm">The requested application could not be found</div>
        <Button onClick={() => navigate('/apps')} className="mt-4">
          Back to Apps
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
        <button
          onClick={() => navigate('/apps')}
          className="hover:text-gray-700 dark:hover:text-gray-200"
        >
          Applications
        </button>
        <span>→</span>
        <span className="text-gray-900 dark:text-gray-100">
          {app.name || app.display_name}
        </span>
      </nav>

      {/* App Header */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="text-3xl">
              {getAppTypeIcon(app.app_type)}
            </div>
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {app.name || app.display_name}
                </h1>
                <AppStatus status={getAppStatus(app)} />
              </div>
              {app.description && (
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  {app.description}
                </p>
              )}
              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span>Type: {app.app_type_display || 'Unknown'}</span>
                <span>•</span>
                <span>Client ID: {app.client_id || app.app_id || 'N/A'}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleSyncApp}
              disabled={syncing}
              variant="secondary"
              size="sm"
            >
              {syncing ? '🔄 Syncing...' : '🔄 Sync from Auth0'}
            </Button>
            <Button
              onClick={() => navigate('/apps')}
              variant="secondary"
              size="sm"
            >
              Back to Apps
            </Button>
          </div>
        </div>

        {/* Sync Error */}
        {syncError && (
          <div className="mt-4">
            <ErrorMessage title="Sync Error" message={syncError} />
          </div>
        )}
      </Card>

      {/* App Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {feedbackTotal}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Total Feedback
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {app.is_active ? 'Active' : 'Inactive'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Status
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {feedback.length > 0 ? formatDate(feedback[0].created_at) : 'Never'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Last Feedback
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
            {formatDate(app.last_synced_at)}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Last Synced
          </div>
        </Card>
      </div>

      {/* Feedback Table */}
      <Card className="p-6">
        <FeedbackTable
          feedbackList={feedback}
          total={feedbackTotal}
          loading={feedbackLoading}
          error={feedbackError || undefined}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={10}
          onPageChange={fetchFeedback}
          showFilters={true}
          showPagination={true}
        />
      </Card>
    </div>
  );
};

export default AppFeedbackPage;